import React, { useEffect, useRef, useState } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Alert,
} from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import { sendEmailVerification } from 'firebase/auth';
import { auth } from '@/shared/services/firebase';
import { isEmailVerified } from '@/shared/utils/emailVerified';
import usePageTitle from '@/shared/hooks/usePageTitle';
import MicrosoftSignInButton from './MicrosoftSignInButton';

/**
 * Message for a failed link attempt. The password mistakes reuse the sign-in
 * wording so the page never says whether an account exists.
 *
 * @param {{ code?: string }} err
 * @returns {string}
 */
export function linkErrorMessage(err) {
    // The link succeeded but this environment does not accept the password
    // session it was made with; the context has already set the notice that
    // says so, and the login page shows it with the Microsoft button.
    if (err?.code === 'auth/linked-sign-in-required') {
        return '';
    }
    // The same code means a wrong password from the sign-in step and an
    // expired or malformed Microsoft credential from the link step; only the
    // second is fixed by starting the Microsoft sign-in again.
    if (err?.linkStage && err.code === 'auth/invalid-credential') {
        return 'The Microsoft sign-in has expired. Cancel, then use the Microsoft button again.';
    }
    switch (err?.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
            return 'Invalid email or password.';
        case 'auth/too-many-requests':
            return 'Too many failed attempts. Please try again later.';
        case 'auth/credential-already-in-use':
            return 'That Microsoft account is already linked to a different Hub account.';
        case 'auth/provider-already-linked':
            return 'Microsoft sign-in is already linked to this account. Use the Microsoft button to sign in.';
        default:
            return 'Failed to link Microsoft sign-in. Please try again.';
    }
}

function LogIn() {
    usePageTitle('Log in');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    // Set when a Microsoft sign-in met an existing password account for the
    // same address: the email and the Microsoft credential to link once the
    // password is proved.
    const [linkRequest, setLinkRequest] = useState(null);
    const [linkPassword, setLinkPassword] = useState('');
    const linkHeadingRef = useRef(null);

    // The sign-in form is replaced without a navigation, so move focus to the
    // new heading: a screen reader then reads what changed and why, and the
    // password field is one Tab away.
    useEffect(() => {
        if (linkRequest && linkHeadingRef.current) {
            linkHeadingRef.current.focus();
        }
    }, [linkRequest]);

    const {
        login,
        linkMicrosoftToPasswordAccount,
        authMethods,
        authNotice,
        clearAuthNotice,
    } = useAuth();
    // Which methods this environment accepts, from the server.
    const methods = authMethods;
    const passwordEnabled = methods.includes('password');
    const microsoftEnabled = methods.includes('microsoft');
    const navigate = useNavigate();
    const location = useLocation();

    // Check for redirect from query string (set by ProtectedRoutes)
    const params = new URLSearchParams(location.search);
    const redirectTo = params.get('redirect') || '/listings';

    async function handleSubmit(e) {
        e.preventDefault();

        try {
            setError('');
            setMessage('');
            setLoading(true);
            const userCredential = await login(email, password);

            if (!isEmailVerified(userCredential.user)) {
                setError(
                    'Please verify your email before logging in. Check your inbox for a verification link.'
                );
                return;
            }

            navigate(redirectTo, { replace: true });
        } catch (err) {
            if (
                err.code === 'auth/user-not-found' ||
                err.code === 'auth/wrong-password'
            ) {
                setError('Invalid email or password.');
            } else if (err.code === 'auth/invalid-credential') {
                setError('Invalid email or password.');
            } else if (err.code === 'auth/too-many-requests') {
                setError('Too many failed attempts. Please try again later.');
            } else {
                setError('Failed to sign in. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }

    function handleLinkRequired(request) {
        setError('');
        setMessage('');
        setLinkPassword('');
        setLinkRequest(request);
    }

    function handleLinkCancel() {
        setLinkRequest(null);
        setLinkPassword('');
        setError('');
    }

    async function handleLinkSubmit(e) {
        e.preventDefault();

        try {
            setError('');
            setLoading(true);
            await linkMicrosoftToPasswordAccount(
                linkRequest.email,
                linkPassword,
                linkRequest.pendingCredential
            );
            navigate(redirectTo, { replace: true });
        } catch (err) {
            const message = linkErrorMessage(err);
            if (message) {
                setError(message);
            } else {
                handleLinkCancel();
            }
        } finally {
            setLoading(false);
        }
    }

    async function handleResendVerification() {
        try {
            setMessage('');
            setError('');
            if (auth.currentUser && !isEmailVerified(auth.currentUser)) {
                await sendEmailVerification(auth.currentUser);
                setMessage('Verification email sent! Please check your inbox.');
            } else {
                setError(
                    'Please log in first, then resend the verification email.'
                );
            }
        } catch (err) {
            if (err.code === 'auth/too-many-requests') {
                setError(
                    'Please wait before requesting another verification email.'
                );
            } else {
                setError(
                    'Failed to send verification email. Please try again.'
                );
            }
        }
    }

    return (
        <Container className="py-5">
            <Row className="justify-content-center">
                <Col md={6} lg={5}>
                    <Card>
                        <Card.Body className="p-4">
                            <h1 className="h2 text-center mb-4">Log In</h1>
                            {authNotice && !error && (
                                <Alert
                                    variant="warning"
                                    dismissible
                                    onClose={clearAuthNotice}
                                >
                                    {authNotice}
                                </Alert>
                            )}
                            {error && (
                                <Alert variant="danger">
                                    {error}
                                    {error.includes('verify your email') && (
                                        <div className="mt-2">
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0"
                                                onClick={
                                                    handleResendVerification
                                                }
                                            >
                                                Resend verification email
                                            </Button>
                                        </div>
                                    )}
                                </Alert>
                            )}
                            {message && (
                                <Alert variant="success">{message}</Alert>
                            )}
                            {linkRequest ? (
                                <Form onSubmit={handleLinkSubmit}>
                                    <h2
                                        className="h5 mb-2"
                                        ref={linkHeadingRef}
                                        tabIndex={-1}
                                    >
                                        Add Microsoft sign-in to your account
                                    </h2>
                                    <p className="text-muted">
                                        An account for{' '}
                                        <strong>{linkRequest.email}</strong>{' '}
                                        already uses a password. Enter it once
                                        to link Microsoft sign-in; after that,
                                        either method opens the same account.
                                    </p>
                                    {/* Read-only address so a password manager
                                        can match the saved login for it. */}
                                    <Form.Group
                                        className="mb-3"
                                        controlId="linkEmail"
                                    >
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={linkRequest.email}
                                            autoComplete="username"
                                            readOnly
                                        />
                                    </Form.Group>
                                    <Form.Group
                                        className="mb-3"
                                        controlId="linkPassword"
                                    >
                                        <Form.Label>Password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            value={linkPassword}
                                            onChange={(e) =>
                                                setLinkPassword(e.target.value)
                                            }
                                            autoComplete="current-password"
                                            required
                                        />
                                    </Form.Group>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="w-100"
                                        disabled={loading}
                                    >
                                        {loading
                                            ? 'Linking...'
                                            : 'Link and sign in'}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="link"
                                        className="w-100 mt-2"
                                        onClick={handleLinkCancel}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </Button>
                                </Form>
                            ) : (
                                <>
                                    {passwordEnabled && (
                                        <Form onSubmit={handleSubmit}>
                                            <Form.Group
                                                className="mb-3"
                                                controlId="email"
                                            >
                                                <Form.Label>Email</Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    autoComplete="username"
                                                    value={email}
                                                    onChange={(e) =>
                                                        setEmail(e.target.value)
                                                    }
                                                    required
                                                />
                                            </Form.Group>
                                            <Form.Group
                                                className="mb-3"
                                                controlId="password"
                                            >
                                                <Form.Label>
                                                    Password
                                                </Form.Label>
                                                <Form.Control
                                                    type="password"
                                                    autoComplete="current-password"
                                                    value={password}
                                                    onChange={(e) =>
                                                        setPassword(
                                                            e.target.value
                                                        )
                                                    }
                                                    required
                                                />
                                            </Form.Group>
                                            <Button
                                                type="submit"
                                                variant="primary"
                                                className="w-100"
                                                disabled={loading}
                                            >
                                                {loading
                                                    ? 'Signing in...'
                                                    : 'Log In'}
                                            </Button>
                                        </Form>
                                    )}
                                    {passwordEnabled && microsoftEnabled && (
                                        <div
                                            className="d-flex align-items-center my-3"
                                            aria-hidden="true"
                                        >
                                            <hr className="flex-grow-1" />
                                            <span className="px-2 text-muted small">
                                                or
                                            </span>
                                            <hr className="flex-grow-1" />
                                        </div>
                                    )}
                                    {microsoftEnabled && (
                                        <MicrosoftSignInButton
                                            label="Sign in with your MSU Denver account"
                                            redirectTo={redirectTo}
                                            disabled={loading}
                                            onError={setError}
                                            onLinkRequired={handleLinkRequired}
                                        />
                                    )}
                                    {passwordEnabled && (
                                        <div className="text-center mt-3">
                                            <Link to="/forgot-password">
                                                Forgot Password?
                                            </Link>
                                        </div>
                                    )}
                                </>
                            )}
                        </Card.Body>
                    </Card>
                    {passwordEnabled && (
                        <p className="text-center mt-3">
                            Don&apos;t have an account?{' '}
                            <Link to="/signup">Sign Up</Link>
                        </p>
                    )}
                </Col>
            </Row>
        </Container>
    );
}

export default LogIn;
